import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { Card } from '../../components/ui/Card';
import { api } from '../../lib/api';
import { Series, type Point } from './Series';

/**
 * What the moderation and audio service did for this account, from its own
 * account API.
 *
 * ## The same page as Traffic, for a different vendor
 *
 * Traffic reads Cloudflare; this reads IR8. Both are third parties whose
 * numbers matter to the person running the site and are otherwise only on
 * the vendor's dashboard. Both are read server-side with a read-only key,
 * cached briefly, and answer "nothing to show" rather than failing. The
 * layouts match on purpose: somebody who has read one can read the other.
 *
 * ## What is worth a colour
 *
 * Most of this is counts. Three things are health: server errors in the last
 * day, a queue that is not draining, and degraded verdicts (a provider
 * outage pushed a check down a tier). Those get a tone; everything else is
 * ink, so the tones mean something when they appear.
 *
 * ## Jobs, for one question
 *
 * The job list at the bottom exists for "what did the service do with this
 * upload, and if it failed, why". The ref filter takes the same `track:{id}`
 * and `comment:{id}` refs the platform sends, and each row links to the
 * service's own page for the full result.
 */
interface Overview {
  available: boolean;
  reason: 'not_configured' | 'wrong_key' | 'unavailable' | null;
  days: number;
  fetched_at: string | null;
  account: {
    account: {
      name: string;
      plan: string;
      status: string;
      rate_limit_rpm: number;
      features: string[];
    };
    usage_mtd: { kind: string; units: number; records: number }[];
    today: { kind: string; status: string; n: number }[];
    queue: { queued: number; running: number };
    requests_24h: { total: number; server_errors: number; client_errors: number; avg_ms: number };
    active_keys: number;
  } | null;
  moderation: {
    checks: number;
    flagged: number;
    flag_rate: number;
    cached: number;
    degraded: number;
    avg_ms: number;
    by_tier: { tier: string; checks: number; flagged: number }[];
    by_category: { category: string; flagged: number }[];
    by_day: { day: string; checks: number; flagged: number }[];
    languages: { language: string; checks: number }[];
  } | null;
  audio: {
    jobs: number;
    succeeded: number;
    failed: number;
    avg_ms: number;
    audio_seconds: number;
    with_fingerprint_matches: number;
    transcribed: number;
    explicit: number;
    failures: { code: string; jobs: number }[];
    genres: { genre: string; tracks: number }[];
  } | null;
  usage: {
    rows: { period: string; kind: string; units: number; records: number }[];
    totals: Record<string, { units: number; records: number }>;
  } | null;
}

interface JobRow {
  job_id: string;
  kind: string;
  status: string;
  external_ref: string | null;
  queued_at: string;
  finished_at: string | null;
  duration_ms: number | null;
  error_code: string | null;
  flagged: boolean | null;
}

interface Jobs {
  available: boolean;
  data: JobRow[];
  page: number;
  per_page: number;
  total: number;
}

const WINDOWS = [
  { days: 7, label: '7 days' },
  { days: 30, label: '30 days' },
  { days: 90, label: '90 days' },
];

const DASHBOARD = 'https://dashboard.irate.tech';

/** The table convention for "no value", spelled so it is not read as prose. */
const NONE = String.fromCharCode(0x2014);

export function AdminIrateTechPage() {
  const [days, setDays] = useState(30);

  const { data, isPending, isError } = useQuery({
    queryKey: ['admin-iratetech', days],
    queryFn: () => api.get<Overview>(`/admin/iratetech/overview?days=${days}`),
    // Cached a minute server-side; a refetch per tab switch would be handed
    // the same answer back.
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });

  return (
    <div className="flex flex-col gap-6" data-admin-iratetech>
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="flex flex-col gap-1">
          <h1 className="text-2xl font-semibold tracking-tight text-ink">IR8</h1>
          <p className="max-w-2xl text-[13px] leading-relaxed text-soft">
            What the moderation and audio service did for this account: every chat line and
            comment it read, every upload it measured, and what it cost. Read with its own
            read-only key.
          </p>
        </div>

        <div
          role="radiogroup"
          aria-label="Time window"
          className="flex shrink-0 items-center gap-1 rounded-[var(--radius-pill)] border border-line p-1"
        >
          {WINDOWS.map((window) => (
            <button
              key={window.days}
              type="button"
              role="radio"
              aria-checked={days === window.days}
              data-window={window.days}
              onClick={() => setDays(window.days)}
              className={`rounded-[var(--radius-pill)] px-3 py-1 font-mono text-[11px] transition-colors ${
                days === window.days
                  ? 'bg-accent text-accent-ink'
                  : 'text-soft hover:bg-raised hover:text-ink'
              }`}
            >
              {window.label}
            </button>
          ))}
        </div>
      </div>

      {isPending && <p className="font-mono text-[11px] text-faint">Asking the service…</p>}

      {!isPending && (isError || data?.available === false) && (
        <Card className="flex flex-col gap-2 p-5" data-iratetech-unavailable>
          <span className="meta-caps text-ghost">Not connected</span>
          <p className="text-[13px] leading-relaxed text-soft">
            {data?.reason === 'not_configured'
              ? 'There is no account key configured. It is a second key with only the account:read scope, set as IRATETECH_ADMIN_API_KEY beside the others.'
              : data?.reason === 'wrong_key'
                ? 'The key configured cannot read account stats. It needs the account:read scope, which the moderation key deliberately does not have.'
                : 'The service did not answer. Nothing is wrong with the site; the numbers will be back when it is.'}
          </p>
        </Card>
      )}

      {data?.available && data.account && data.moderation && data.audio && data.usage && (
        <>
          <Health account={data.account} degraded={data.moderation.degraded} />

          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Figure value={data.moderation.checks} label="checks read" />
            <Figure
              value={`${(data.moderation.flag_rate * 100).toFixed(1)}%`}
              label={`flagged · ${data.moderation.flagged.toLocaleString()}`}
              tone={data.moderation.flag_rate > 0.15 ? 'text-hot-text' : 'text-accent'}
            />
            <Figure value={data.audio.jobs} label="uploads measured" />
            <Figure
              value={data.audio.with_fingerprint_matches}
              label="matched an earlier upload"
              tone={data.audio.with_fingerprint_matches > 0 ? 'text-warning' : 'text-ink'}
            />
          </div>

          {/* -------------------------------------------------------- */}
          {/* Moderation                                               */}
          {/* -------------------------------------------------------- */}
          <section className="flex flex-col gap-4" data-iratetech-moderation>
            <h2 className="meta-caps text-faint">moderation</h2>

            <Card className="flex flex-col gap-4 p-5">
              <div className="flex items-baseline justify-between gap-3">
                <span className="meta-caps text-ghost">Flagged by day</span>
                <span className="font-mono text-[10px] text-ghost">
                  {data.moderation.cached.toLocaleString()} answered from cache ·{' '}
                  {data.moderation.avg_ms}ms average
                </span>
              </div>
              <Series points={byDay(data.moderation.by_day, 'flagged')} label="flag" tone="hot" />
            </Card>

            <div className="grid gap-4 lg:grid-cols-3">
              <Breakdown
                title="Categories"
                hint="a check can carry more than one"
                rows={data.moderation.by_category.map((r) => ({
                  label: r.category.replace(/_/g, ' '),
                  value: r.flagged,
                }))}
                empty="Nothing flagged in this window."
              />
              <Breakdown
                title="Which tier decided"
                hint="local is the word list; it should stay small"
                rows={data.moderation.by_tier.map((r) => ({
                  label: r.tier,
                  value: r.checks,
                  note: `${r.flagged} flagged`,
                }))}
                empty="No checks in this window."
              />
              <Breakdown
                title="Languages"
                rows={data.moderation.languages.map((r) => ({ label: r.language, value: r.checks }))}
                empty="No checks in this window."
              />
            </div>
          </section>

          {/* -------------------------------------------------------- */}
          {/* Audio                                                    */}
          {/* -------------------------------------------------------- */}
          <section className="flex flex-col gap-4" data-iratetech-audio>
            <h2 className="meta-caps text-faint">audio</h2>

            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <Figure
                value={
                  data.audio.jobs > 0
                    ? `${((data.audio.succeeded / data.audio.jobs) * 100).toFixed(0)}%`
                    : '—'
                }
                label={`succeeded · ${data.audio.failed} failed`}
                tone={data.audio.failed > 0 ? 'text-warning' : 'text-fresh'}
              />
              <Figure value={minutes(data.audio.audio_seconds)} label="minutes of audio" />
              <Figure value={`${(data.audio.avg_ms / 1000).toFixed(1)}s`} label="per track" />
              <Figure
                value={data.audio.transcribed}
                label={`transcribed · ${data.audio.explicit} explicit`}
              />
            </div>

            <div className="grid gap-4 lg:grid-cols-2">
              <Breakdown
                title="Failures"
                hint="fetch_failed usually means the presigned link expired"
                rows={data.audio.failures.map((r) => ({ label: r.code, value: r.jobs }))}
                empty="Nothing failed in this window."
              />
              <Breakdown
                title="Genres, as the service hears them"
                hint="eight classes, a curiosity"
                rows={data.audio.genres.map((r) => ({
                  label: r.genre.replace(/_/g, ' '),
                  value: r.tracks,
                }))}
                empty="Nothing measured in this window."
              />
            </div>
          </section>

          {/* -------------------------------------------------------- */}
          {/* Usage                                                    */}
          {/* -------------------------------------------------------- */}
          <section className="flex flex-col gap-4" data-iratetech-usage>
            <h2 className="meta-caps text-faint">usage</h2>

            <div className="grid gap-4 lg:grid-cols-2">
              <Card className="flex flex-col gap-4 p-5">
                <div className="flex items-baseline justify-between gap-3">
                  <span className="meta-caps text-ghost">Moderation checks by day</span>
                  <span className="font-mono text-[10px] text-ghost">requests</span>
                </div>
                <Series
                  points={usageByDay(data.usage.rows, 'moderation.check')}
                  label="check"
                />
              </Card>
              <Card className="flex flex-col gap-4 p-5">
                <div className="flex items-baseline justify-between gap-3">
                  <span className="meta-caps text-ghost">Audio measured by day</span>
                  <span className="font-mono text-[10px] text-ghost">minutes</span>
                </div>
                <Series
                  points={usageByDay(data.usage.rows, 'audio.analyze', 60)}
                  label="minute"
                  tone="fresh"
                />
              </Card>
            </div>

            <Breakdown
              title={`Totals · last ${data.days} days`}
              hint="seconds for audio, requests for moderation"
              rows={Object.entries(data.usage.totals).map(([kind, t]) => ({
                label: kind,
                value: Math.round(t.units),
                note: `${t.records.toLocaleString()} jobs`,
              }))}
              empty="No usage in this window."
            />
          </section>

          <JobsTable />

          <p className="font-mono text-[10px] leading-relaxed text-ghost">
            Read from the service with a key that can only read. Cached for a minute. Reading
            these numbers is not metered.{' '}
            <a href={DASHBOARD} target="_blank" rel="noreferrer" className="hover:text-soft hover:underline">
              The full dashboard is at dashboard.irate.tech.
            </a>
          </p>
        </>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */

/**
 * The account's health right now, as one row.
 *
 * Month-to-date usage beside it because "how much are we using" and "is it
 * working" are the two questions anybody opens this page with, and they
 * should be answered before the scroll.
 */
function Health({ account, degraded }: { account: NonNullable<Overview['account']>; degraded: number }) {
  const today = account.today;
  const ok = today.filter((t) => t.status === 'succeeded').reduce((n, t) => n + t.n, 0);
  const bad = today.filter((t) => t.status === 'failed').reduce((n, t) => n + t.n, 0);
  const rate = ok + bad > 0 ? (ok / (ok + bad)) * 100 : null;
  const errors = account.requests_24h.server_errors;
  const backlog = account.queue.queued;

  const mtd = (kind: string) => account.usage_mtd.find((u) => u.kind === kind);

  return (
    <Card className="flex flex-col gap-4 p-5" data-iratetech-health>
      <div className="flex items-baseline justify-between gap-3">
        <span className="meta-caps text-ghost">Right now</span>
        <span className="font-mono text-[10px] text-ghost">
          {account.account.plan} · {account.account.rate_limit_rpm}/min · {account.active_keys}{' '}
          keys
        </span>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
        <Tile
          value={rate === null ? '—' : `${rate.toFixed(0)}%`}
          label="succeeded today"
          tone={bad > 0 ? 'text-warning' : 'text-fresh'}
        />
        <Tile
          value={backlog + account.queue.running}
          label={`in the queue · ${account.queue.running} running`}
          tone={backlog > 20 ? 'text-hot-text' : 'text-ink'}
        />
        <Tile
          value={errors}
          label="server errors, 24h"
          tone={errors > 0 ? 'text-hot-text' : 'text-fresh'}
        />
        <Tile value={`${account.requests_24h.avg_ms}ms`} label="average answer, 24h" />
        <Tile
          value={degraded}
          label="degraded verdicts"
          tone={degraded > 5 ? 'text-warning' : 'text-ink'}
        />
        <Tile value={account.requests_24h.total} label="requests, 24h" />
      </div>

      <div className="flex flex-wrap gap-x-6 gap-y-1 border-t border-line pt-3 font-mono text-[11px] text-soft">
        <span className="text-ghost">this month</span>
        <span>{(mtd('moderation.check')?.units ?? 0).toLocaleString()} checks</span>
        <span>{minutes(mtd('audio.analyze')?.units ?? 0)} min measured</span>
        {mtd('audio.transcribe') && (
          <span>{minutes(mtd('audio.transcribe')?.units ?? 0)} min transcribed</span>
        )}
        {mtd('moderation.name') && <span>{mtd('moderation.name')?.units ?? 0} names</span>}
      </div>
    </Card>
  );
}

/**
 * The service's job list, filtered.
 *
 * Its own query, its own state: the page's numbers are a minute stale by
 * design and this is not, because the question it answers is about a job
 * that may be running.
 */
function JobsTable() {
  const [kind, setKind] = useState('');
  const [status, setStatus] = useState('');
  const [ref, setRef] = useState('');
  const [applied, setApplied] = useState('');
  const [page, setPage] = useState(1);

  const params = new URLSearchParams({ page: String(page), per_page: '25' });

  if (kind) params.set('kind', kind);
  if (status) params.set('status', status);
  if (applied) params.set('external_ref', applied);

  const { data, isPending } = useQuery({
    queryKey: ['admin-iratetech-jobs', params.toString()],
    queryFn: () => api.get<Jobs>(`/admin/iratetech/jobs?${params.toString()}`),
    refetchOnWindowFocus: false,
  });

  const pages = data ? Math.max(1, Math.ceil(data.total / data.per_page)) : 1;

  const pick =
    'rounded-[var(--radius-control)] border border-line bg-surface px-2.5 py-1.5 font-mono text-[11px] text-ink outline-none focus:border-line-lit';

  return (
    <Card className="flex flex-col gap-4 p-5" data-iratetech-jobs>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <span className="meta-caps text-ghost">Jobs</span>

        <form
          className="flex flex-wrap items-center gap-2"
          onSubmit={(event) => {
            event.preventDefault();
            setPage(1);
            setApplied(ref.trim());
          }}
        >
          <select
            value={kind}
            aria-label="Kind"
            className={pick}
            onChange={(event) => {
              setKind(event.target.value);
              setPage(1);
            }}
          >
            <option value="">every kind</option>
            <option value="moderation.check">moderation</option>
            <option value="audio.analyze">audio</option>
          </select>
          <select
            value={status}
            aria-label="Status"
            className={pick}
            onChange={(event) => {
              setStatus(event.target.value);
              setPage(1);
            }}
          >
            <option value="">every status</option>
            <option value="succeeded">succeeded</option>
            <option value="failed,cancelled">failed</option>
            <option value="queued,running">in progress</option>
          </select>
          <input
            value={ref}
            placeholder="track:24 or comment:991"
            aria-label="Reference"
            data-jobs-ref
            className={`${pick} w-44`}
            onChange={(event) => setRef(event.target.value)}
          />
          <button
            type="submit"
            className="rounded-[var(--radius-pill)] border border-line px-3 py-1.5 font-mono text-[11px] text-soft transition-colors hover:border-line-lit hover:text-ink"
          >
            find
          </button>
        </form>
      </div>

      {isPending && <p className="font-mono text-[11px] text-faint">Asking…</p>}

      {data && data.data.length === 0 && (
        <p className="font-mono text-[11px] text-faint">
          {applied ? `Nothing for ${applied}.` : 'No jobs match.'}
        </p>
      )}

      {data && data.data.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-left font-mono text-[11px]">
            <thead className="text-ghost">
              <tr>
                <th className="py-1.5 pr-3 font-normal">when</th>
                <th className="py-1.5 pr-3 font-normal">kind</th>
                <th className="py-1.5 pr-3 font-normal">ref</th>
                <th className="py-1.5 pr-3 font-normal">status</th>
                <th className="py-1.5 pr-3 text-right font-normal">took</th>
                <th className="py-1.5 font-normal">job</th>
              </tr>
            </thead>
            <tbody className="text-soft">
              {data.data.map((job) => (
                <tr key={job.job_id} className="border-t border-line" data-job-row={job.job_id}>
                  <td className="whitespace-nowrap py-1.5 pr-3 text-faint">{when(job.queued_at)}</td>
                  <td className="py-1.5 pr-3">{job.kind.replace('.', ' ')}</td>
                  <td className="py-1.5 pr-3">
                    <RefLink value={job.external_ref} />
                  </td>
                  <td className="py-1.5 pr-3">
                    <JobStatus job={job} />
                  </td>
                  <td className="py-1.5 pr-3 text-right tabular-nums">
                    {job.duration_ms === null ? '—' : `${job.duration_ms.toLocaleString()}ms`}
                  </td>
                  <td className="py-1.5">
                    <a
                      href={`${DASHBOARD}/jobs/${job.job_id}`}
                      target="_blank"
                      rel="noreferrer"
                      className="text-accent hover:underline"
                      title={job.job_id}
                    >
                      {job.job_id.slice(-6)} ↗
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {data && pages > 1 && (
        <div className="flex items-center justify-end gap-3 font-mono text-[11px] text-faint">
          <span>
            {data.total.toLocaleString()} jobs · page {page} of {pages}
          </span>
          <button
            type="button"
            disabled={page <= 1}
            onClick={() => setPage((p) => p - 1)}
            className="text-soft hover:text-ink disabled:opacity-40"
          >
            ‹ prev
          </button>
          <button
            type="button"
            disabled={page >= pages}
            onClick={() => setPage((p) => p + 1)}
            className="text-soft hover:text-ink disabled:opacity-40"
          >
            next ›
          </button>
        </div>
      )}
    </Card>
  );
}

/** A ref the platform sent, linked back to the thing it names where there is a page. */
function RefLink({ value }: { value: string | null }) {
  if (value === null) return <span className="text-faint">{NONE}</span>;

  const track = /^track:(\d+)$/.exec(value);

  if (track) {
    return (
      <Link to={`/tracks/${track[1]}`} className="hover:text-ink hover:underline">
        {value}
      </Link>
    );
  }

  return <span>{value}</span>;
}

function JobStatus({ job }: { job: JobRow }) {
  if (job.status === 'failed' || job.status === 'cancelled') {
    return <span className="text-hot-text">{job.error_code ?? job.status}</span>;
  }

  if (job.status === 'succeeded') {
    return (
      <span className={job.flagged ? 'text-warning' : 'text-fresh'}>
        {job.flagged ? 'flagged' : 'ok'}
      </span>
    );
  }

  return <span className="text-faint">{job.status}</span>;
}

/* ------------------------------------------------------------------ */

function byDay(rows: { day: string; checks: number; flagged: number }[], key: 'checks' | 'flagged'): Point[] {
  return rows.map((row) => ({ date: row.day, value: row[key] }));
}

/**
 * One kind's usage as a daily series. The service only sends rows for days
 * with usage, so the gaps are filled with zero - a chart that skips quiet
 * days reads as busier than it was.
 */
function usageByDay(
  rows: { period: string; kind: string; units: number }[],
  kind: string,
  divide = 1,
): Point[] {
  const mine = rows.filter((row) => row.kind === kind);

  if (mine.length === 0) return [];

  const byPeriod = new Map(mine.map((row) => [row.period, row.units / divide]));
  const periods = mine.map((row) => row.period).sort();
  const first = new Date(periods[0] ?? '');
  const last = new Date(periods[periods.length - 1] ?? '');
  const points: Point[] = [];

  for (let at = first; at <= last; at = new Date(at.getTime() + 86_400_000)) {
    const date = at.toISOString().slice(0, 10);
    points.push({ date, value: Math.round(byPeriod.get(date) ?? 0) });
  }

  return points;
}

function minutes(seconds: number): string {
  return Math.round(seconds / 60).toLocaleString();
}

function when(iso: string): string {
  const d = new Date(iso);

  return `${d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} ${d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}`;
}

function Figure({ value, label, tone = 'text-ink' }: { value: number | string; label: string; tone?: string }) {
  return (
    <Card className="flex flex-col gap-1 p-4">
      <span className={`text-2xl font-bold tracking-tight ${tone}`}>
        {typeof value === 'number' ? value.toLocaleString() : value}
      </span>
      <span className="font-mono text-[10px] text-faint">{label}</span>
    </Card>
  );
}

function Tile({ value, label, tone = 'text-ink' }: { value: number | string; label: string; tone?: string }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className={`text-lg font-bold tabular-nums ${tone}`}>
        {typeof value === 'number' ? value.toLocaleString() : value}
      </span>
      <span className="font-mono text-[10px] text-faint">{label}</span>
    </div>
  );
}

/** One list with a bar behind each row, proportional to the biggest. */
function Breakdown({
  title,
  hint,
  rows,
  empty,
}: {
  title: string;
  hint?: string;
  rows: { label: string; value: number; note?: string }[];
  empty: string;
}) {
  const peak = Math.max(1, ...rows.map((row) => row.value));

  return (
    <Card className="flex flex-col gap-3 p-5" data-breakdown={title}>
      <div className="flex items-baseline justify-between gap-3">
        <span className="meta-caps text-ghost">{title}</span>
        {hint !== undefined && <span className="font-mono text-[10px] text-ghost">{hint}</span>}
      </div>

      {rows.length === 0 ? (
        <p className="font-mono text-[11px] text-faint">{empty}</p>
      ) : (
        <ol className="flex flex-col gap-1.5">
          {rows.map((row) => (
            <li key={row.label} className="relative flex items-center justify-between gap-3 py-1">
              <span
                aria-hidden
                className="absolute inset-y-0 left-0 rounded-[3px] bg-raised"
                style={{ width: `${Math.max(2, (row.value / peak) * 100)}%` }}
              />
              <span className="relative min-w-0 truncate px-2 font-mono text-[11px] text-soft">
                {row.label || '(none)'}
              </span>
              <span className="relative shrink-0 px-2 font-mono text-[11px] tabular-nums text-ink">
                {row.value.toLocaleString()}
                {row.note !== undefined && <span className="ml-2 text-faint">{row.note}</span>}
              </span>
            </li>
          ))}
        </ol>
      )}
    </Card>
  );
}
